import { Point, Polygon, Layer, IconType, GlobalContext } from "../../../context/context";
import { 
    getDistanceMeters,
    pointInPolygon,
    fetchRoadWaysInPolygon
} from '../utils';
import { OpenStreetMapService } from '../../../services/osm/OpenStreetMapService';

interface MeetingPointsHandlerProps {
    distance: number;
    roadTypes: string[];
    selectedIcon: IconType;
    selectedColor: string;
    iconSize: number;
    includeBusStops: boolean;
    excludeNearBusStops: boolean;
    exclusionDistance: number;
    polygon: Polygon;
    global: GlobalContext;
    setMeetingPointsLoading: (loading: boolean) => void;
    setIsMeetingPointsModalOpen: (open: boolean) => void;
    setMeetingPointsPolygon: (polygon: Polygon | null) => void;
    onLayerCreated?: (layerId: number, layerName: string) => void;
    onAddressLookupProgress?: (current: number, total: number, message: string) => void;
    abortSignal?: AbortSignal;
}

/**
 * Generate a grid of points within the polygon bounds
 */
function generateGridPoints(polygon: [number, number][], distance: number): [number, number][] {
    const lats = polygon.map(([lat]) => lat);
    const lngs = polygon.map(([, lng]) => lng);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);

    // Convert distance from meters to approximate lat/lng degrees
    // Rough approximation: 1 degree lat ≈ 111km, 1 degree lng ≈ 111km * cos(lat)
    const avgLat = (minLat + maxLat) / 2;
    const latStep = distance / 111000; // meters to degrees
    const lngStep = distance / (111000 * Math.cos(avgLat * Math.PI / 180));

    const gridPoints: [number, number][] = [];
    
    for (let lat = minLat; lat <= maxLat; lat += latStep) {
        for (let lng = minLng; lng <= maxLng; lng += lngStep) {
            const point: [number, number] = [lat, lng];
            if (pointInPolygon(point, polygon)) {
                gridPoints.push(point);
            }
        }
    }

    return gridPoints;
}

/**
 * Find the nearest point on any road to a given location
 */
function findNearestRoadPoint(
    point: [number, number], 
    roadWays: [number, number][][],
    maxDistance: number = 500 // Maximum distance to snap to road (meters)
): [number, number] | null {
    let nearestPoint: [number, number] | null = null;
    let minDistance = maxDistance;

    for (const way of roadWays) {
        for (let i = 0; i < way.length - 1; i++) {
            const [lat1, lng1] = way[i];
            const [lat2, lng2] = way[i + 1];
            
            // Calculate the closest point on this road segment
            const closestPoint = getClosestPointOnSegment(point, [lat1, lng1], [lat2, lng2]);
            const distance = getDistanceMeters(point, closestPoint);
            
            if (distance < minDistance) {
                minDistance = distance;
                nearestPoint = closestPoint;
            }
        }
    }

    return nearestPoint;
}

/**
 * Calculate the closest point on a line segment to a given point
 */
function getClosestPointOnSegment(
    point: [number, number],
    lineStart: [number, number],
    lineEnd: [number, number]
): [number, number] {
    const [px, py] = point;
    const [x1, y1] = lineStart;
    const [x2, y2] = lineEnd;

    const A = px - x1;
    const B = py - y1;
    const C = x2 - x1;
    const D = y2 - y1;

    const dot = A * C + B * D;
    const lenSq = C * C + D * D;
    
    if (lenSq === 0) return lineStart; // Line segment is a point

    let param = dot / lenSq;

    if (param < 0) return lineStart;
    if (param > 1) return lineEnd;

    return [x1 + param * C, y1 + param * D];
}

/**
 * Process address lookups in batches to respect rate limits and provide progress feedback
 */
async function processAddressLookupsInBatches(
    coordinates: [number, number][],
    osmService: OpenStreetMapService,
    selectedColor: string,
    selectedIcon: IconType,
    iconSize: number,
    onProgress?: (current: number, total: number, message: string) => void,
    abortSignal?: AbortSignal
): Promise<Point[]> {
    const BATCH_SIZE = 5; // Process 5 addresses at a time to respect rate limits
    const BATCH_DELAY = 1000; // 1 second delay between batches
    
    const results: Point[] = [];
    
    for (let i = 0; i < coordinates.length; i += BATCH_SIZE) {
        // Check for cancellation before processing each batch
        if (abortSignal?.aborted) {
            console.log('Address lookup cancelled by user');
            throw new Error('Address lookup cancelled');
        }
        
        const batch = coordinates.slice(i, i + BATCH_SIZE);
        const batchIndex = Math.floor(i / BATCH_SIZE) + 1;
        const totalBatches = Math.ceil(coordinates.length / BATCH_SIZE);
        const processedCount = results.length;
        
        console.log(`Processing address lookup batch ${batchIndex}/${totalBatches} (${batch.length} addresses)`);
        
        // Report progress with user-friendly messages
        if (onProgress) {
            let message: string;
            if (batchIndex === 1) {
                message = `Starting address lookup for ${coordinates.length} meeting points...`;
            } else if (batchIndex === totalBatches) {
                message = `Finalizing address lookup... Almost done!`;
            } else {
                message = `Looking up addresses... ${batchIndex} of ${totalBatches} batches completed`;
            }
            
            onProgress(
                processedCount,
                coordinates.length,
                message
            );
        }
        
        // Process current batch in parallel
        const batchResults = await Promise.all(
            batch.map(async (coords, batchItemIndex) => {
                const globalIndex = i + batchItemIndex;
                try {
                    const address = await osmService.getAddressForCoordinates(coords);
                    return {
                        id: `meeting-point-${Date.now()}-${globalIndex}`,
                        type: 'point' as const,
                        name: address,
                        description: `Meeting point at ${address}`,
                        colour: selectedColor, // Use selected color for successful address lookup
                        icon: selectedIcon, // Use selected icon for successful address lookup
                        size: iconSize,
                        isLocked: false,
                        coordinates: coords,
                    };
                } catch (error) {
                    console.warn(`Address lookup failed for point ${globalIndex}:`, error);
                    // Fallback to generic name if address lookup fails - use different visual styling
                    return {
                        id: `meeting-point-${Date.now()}-${globalIndex}`,
                        type: 'point' as const,
                        name: 'Meeting Point',
                        description: 'Meeting point (address lookup failed)',
                        colour: '#9e9e9e', // Gray color for failed address lookup
                        icon: 'default' as IconType, // Default icon for failed address lookup
                        size: iconSize * 0.8, // Slightly smaller size for failed lookups
                        isLocked: false,
                        coordinates: coords,
                    };
                }
            })
        );
        
        results.push(...batchResults);
        
        // Report completion of current batch
        if (onProgress) {
            let completionMessage: string;
            if (batchIndex === totalBatches) {
                completionMessage = `Address lookup completed successfully!`;
            } else {
                completionMessage = `Batch ${batchIndex} completed. Processing next batch...`;
            }
            
            onProgress(
                results.length,
                coordinates.length,
                completionMessage
            );
        }
        
        // Add delay between batches (except for the last batch)
        if (i + BATCH_SIZE < coordinates.length) {
            console.log(`Waiting ${BATCH_DELAY}ms before next batch...`);
            await new Promise((resolve, reject) => {
                const timeoutId = setTimeout(resolve, BATCH_DELAY);
                
                // Check for cancellation during delay
                if (abortSignal) {
                    const abortHandler = () => {
                        clearTimeout(timeoutId);
                        reject(new Error('Address lookup cancelled'));
                    };
                    
                    if (abortSignal.aborted) {
                        abortHandler();
                    } else {
                        abortSignal.addEventListener('abort', abortHandler);
                        // Clean up listener when timeout completes
                        setTimeout(() => {
                            abortSignal.removeEventListener('abort', abortHandler);
                        }, BATCH_DELAY);
                    }
                }
            });
        }
    }
    
    // Report final completion
    if (onProgress) {
        onProgress(
            results.length,
            coordinates.length,
            `Address lookup completed for ${results.length} meeting points`
        );
    }
    
    console.log(`Completed address lookup for ${results.length} meeting points`);
    return results;
}

const MEETING_POINT_COUNT_WARNING_THRESHOLD = 100;

export function estimateGridPointCount(polygon: Polygon, distance: number): number {
    if (!polygon.coordinates[0]) return 0;
    const coords = polygon.coordinates[0];
    const lats = coords.map(([lat]) => lat);
    const lngs = coords.map(([, lng]) => lng);
    const minLat = Math.min(...lats), maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs), maxLng = Math.max(...lngs);
    const avgLat = (minLat + maxLat) / 2;
    const latStep = distance / 111000;
    const lngStep = distance / (111000 * Math.cos(avgLat * Math.PI / 180));
    const latCount = Math.ceil((maxLat - minLat) / latStep) + 1;
    const lngCount = Math.ceil((maxLng - minLng) / lngStep) + 1;
    return latCount * lngCount;
}

export { MEETING_POINT_COUNT_WARNING_THRESHOLD };

export async function handleGenerateMeetingPoints({
    distance,
    roadTypes,
    selectedIcon,
    selectedColor,
    iconSize,
    includeBusStops,
    excludeNearBusStops,
    exclusionDistance,
    polygon,
    global,
    setMeetingPointsLoading,
    setIsMeetingPointsModalOpen,
    setMeetingPointsPolygon,
    onLayerCreated,
    onAddressLookupProgress,
    abortSignal
}: MeetingPointsHandlerProps) {
    setMeetingPointsLoading(true);
    try {
        if (!polygon || !polygon.coordinates[0]) {
            throw new Error('No polygon selected');
        }

        // 1. Generate evenly distributed grid points across the entire polygon
        const gridPoints = generateGridPoints(polygon.coordinates[0], distance);
        console.log(`Generated ${gridPoints.length} grid points across the zone`);

        // 2. Fetch all road ways in polygon for snapping
        const roadWays = await fetchRoadWaysInPolygon(polygon.coordinates[0], roadTypes);
        console.log(`Found ${roadWays.length} road ways in the polygon`);

        // 3. Create OSM Service instance for both bus stops and address lookup
        const osmService = new OpenStreetMapService({
            baseUrl: 'https://overpass-api.de',
            rateLimit: {
                requestsPerMinute: 30,
                requestsPerHour: 500,
            },
            cache: {
                ttl: 3600000, // 1 hour
                maxSize: 50,
            },
            timeout: 30000,
        });

        // 4. Fetch bus stops if needed for inclusion or exclusion
        let busStopPoints: Point[] = [];
        let busStopCoordinates: [number, number][] = [];
        
        if (includeBusStops || excludeNearBusStops) {
            try {
                console.log('Fetching bus stops from OSM...');

                const transitData = await osmService.getTransitData(polygon);
                console.log(`Found ${transitData.stops.bus.length} bus stops in the polygon`);

                // Filter bus stops to only include those on selected road types
                const filteredBusStops = transitData.stops.bus.filter(stop => {
                    // For bus stops, we need to check if they're near any of the selected road types
                    // Since OSM bus stops don't directly contain road type info, we'll check proximity
                    const nearestRoadPoint = findNearestRoadPoint(stop.coordinates, roadWays, distance * 0.8);
                    return nearestRoadPoint !== null;
                });

                console.log(`Filtered to ${filteredBusStops.length} bus stops on selected road types`);

                if (includeBusStops) {
                    // Apply distance spacing to bus stops (similar to grid points)
                    const spacedBusStops: Point[] = [];
                    for (const stop of filteredBusStops) {
                        // Find the nearest point on a road
                        const roadPoint = findNearestRoadPoint(stop.coordinates, roadWays, distance * 0.8);
                        if (roadPoint) {
                            // Check if this bus stop is too close to any existing bus stop
                            const isTooClose = spacedBusStops.some(existingStop => 
                                getDistanceMeters(existingStop.coordinates, roadPoint) < distance
                            );
                            
                            if (!isTooClose) {
                                spacedBusStops.push({
                                    id: `bus-stop-${stop.id}-${Date.now()}`,
                                    type: 'point' as const,
                                    name: stop.name || 'Bus Stop',
                                    description: `Bus stop with ${stop.routes.length} routes`,
                                    colour: '#1976d2', // Blue color for bus stops
                                    icon: 'bus' as IconType,
                                    size: iconSize,
                                    isLocked: false,
                                    coordinates: roadPoint, // Use the snapped road point
                                });
                            }
                        }
                    }
                    busStopPoints = spacedBusStops;
                    console.log(`Applied distance spacing: ${busStopPoints.length} bus stops after spacing rules`);
                }

                // For exclusion zones, we need the original bus stop coordinates (not snapped)
                if (excludeNearBusStops) {
                    busStopCoordinates = filteredBusStops.map(stop => stop.coordinates);
                    console.log(`Using ${busStopCoordinates.length} bus stops for exclusion zones`);
                }
            } catch (error) {
                console.warn('Failed to fetch bus stops from OSM:', error);
                // Continue without bus stops if there's an error
            }
        }

        // 5. Snap grid points to nearest roads (within reasonable distance)
        const snappedPoints: [number, number][] = [];
        for (const gridPoint of gridPoints) {
            const roadPoint = findNearestRoadPoint(gridPoint, roadWays, distance * 0.8); // Snap within 80% of distance
            if (roadPoint) {
                // Check if this point should be excluded due to proximity to bus stops
                if (excludeNearBusStops) {
                    const isNearBusStop = busStopCoordinates.some(busStopCoord => 
                        getDistanceMeters(roadPoint, busStopCoord) < exclusionDistance
                    );
                    if (isNearBusStop) {
                        continue; // Skip this point
                    }
                }
                snappedPoints.push(roadPoint);
            }
        }
        console.log(`Snapped ${snappedPoints.length} points to roads${excludeNearBusStops ? ` (after bus stop exclusions)` : ''}`);

        // 6. Combine grid points and bus stops, then deduplicate
        const allPoints: [number, number][] = [...snappedPoints];
        const busStopCoordsForDedup = busStopPoints.map(point => point.coordinates);
        
        // Add bus stop coordinates to the deduplication process
        allPoints.push(...busStopCoordsForDedup);

        // Deduplicate points (remove those within user-set distance of each other)
        const deduped: [number, number][] = [];
        const dedupedBusStops: Point[] = [];
        
        for (let i = 0; i < allPoints.length; i++) {
            const pt = allPoints[i];
            const isBusStop = i >= snappedPoints.length; // Points after snappedPoints are bus stops
            
            // Check if this point is too close to any existing point
            const isDuplicate = deduped.some(existing => getDistanceMeters(existing, pt) < distance);
            
            if (!isDuplicate) {
                deduped.push(pt);
                
                // If this is a bus stop, add it to the bus stops array
                if (isBusStop) {
                    const busStopIndex = i - snappedPoints.length;
                    dedupedBusStops.push(busStopPoints[busStopIndex]);
                }
            }
        }
        
        console.log(`Final meeting points after deduplication: ${deduped.length} (${deduped.length - dedupedBusStops.length} generated + ${dedupedBusStops.length} bus stops)`);

        // 7. Create Point objects for generated meeting points with address lookup
        const generatedPointsCoords = deduped.slice(0, deduped.length - dedupedBusStops.length);
        
        // Generate meeting points with batch-processed address lookup (reusing existing OSM Service)
        let generatedMeetingPoints: Point[];
        try {
            // Report initial progress
            if (onAddressLookupProgress) {
                onAddressLookupProgress(
                    0,
                    generatedPointsCoords.length,
                    `Preparing to look up addresses for ${generatedPointsCoords.length} meeting points...`
                );
            }
            
            generatedMeetingPoints = await processAddressLookupsInBatches(
                generatedPointsCoords,
                osmService,
                selectedColor,
                selectedIcon,
                iconSize,
                onAddressLookupProgress || ((current, total, message) => {
                    // Default progress callback for address lookup
                    console.log(`Address lookup progress: ${current}/${total} - ${message}`);
                }),
                abortSignal
            );
        } catch (error) {
            console.warn('Address lookup process failed, falling back to generic meeting points:', error);
            
            // Report error to user
            if (onAddressLookupProgress) {
                onAddressLookupProgress(
                    generatedPointsCoords.length,
                    generatedPointsCoords.length,
                    'Address lookup failed. Using generic meeting point names.'
                );
            }
            
            // Fallback to generic meeting points if entire address lookup process fails
            generatedMeetingPoints = generatedPointsCoords.map((coords, i) => ({
                id: `meeting-point-${Date.now()}-${i}`,
                type: 'point' as const,
                name: 'Meeting Point',
                description: 'Meeting point (address lookup unavailable)',
                colour: '#9e9e9e', // Gray color for failed address lookup
                icon: 'default' as IconType, // Default icon for failed address lookup
                size: iconSize * 0.8, // Slightly smaller size for failed lookups
                isLocked: false,
                coordinates: coords,
            }));
        }

        // 8. Combine generated points and bus stops
        const allMeetingPoints = [...generatedMeetingPoints, ...dedupedBusStops];

        // 9. Add as new layer
        const newLayerId = global.mapLayers.length;
        const layerName = `Meeting Points${includeBusStops ? ' + Bus Stops' : ''}${excludeNearBusStops ? ` (excluded near bus stops)` : ''}`;
        const newLayer = {
            layerId: newLayerId,
            layerName,
            hidden: false,
            shapes: allMeetingPoints,
        };
        global.setLayers((prev: Layer[]) => [...prev, newLayer]);
        global.setActiveLayerId(newLayerId);
        onLayerCreated?.(newLayerId, layerName);

    } catch (error) {
        console.error('Error generating meeting points:', error);
    } finally {
        setMeetingPointsLoading(false);
        setIsMeetingPointsModalOpen(false);
        setMeetingPointsPolygon(null);
    }
}
